import { Observable, ReplaySubject } from "@reactivex/rxjs";
import { EntityResult, ActionResult } from "./entity-service";

export class EntityContainer<EntityType> {
  private subject: ReplaySubject<EntityType>;
  private subjectHasValue: boolean;
  private pendingOperations: Observable<any>[];

  private reset() {
    this.subject = new ReplaySubject<EntityType>();
    this.subjectHasValue = false;
    this.pendingOperations = [];
  }

  constructor() {
    this.reset();
  }

  public hasValue(): boolean {
    return this.subjectHasValue;
  }

  public getSubject(): Observable<EntityType> {
    return (
      this.subject
        // If there are ANY operations in progress, do not update any subscribers until they are done
        .filter(() => !this.pendingOperations.length)
    );
  }

  public update(operation: Observable<EntityResult<EntityType>>) {
    if (this.subject.hasError) {
      // If an container had an error inside it, we are going to reset the container so it can be re-used for subscriptions
      // Subscriptions to contents any errored containers will be servered and must be re-subscribed via this.subject.
      this.reset();
    }

    this.pendingOperations.push(operation);
    operation.take(1).subscribe(
      (result: EntityResult<EntityType>) => {
        this.clearPendingOperation(operation);
        // Make sure the entity that is returned is a frozen copy so the original in memory is not altered
        let entityValueCopy = Object.freeze(angular.copy(result.entityValue));
        this.subject.next(entityValueCopy);
        this.subjectHasValue = true;
      },
      (error: any) => {
        this.subject.error(error);
        this.subjectHasValue = false;
      }
    );
  }

  public waitWhile<T>(operation: Observable<ActionResult<T>>) {
    this.pendingOperations.push(operation);
    operation.single().subscribe(
      (actionResult: ActionResult<T>) => {
        this.clearPendingOperation(operation);
      },
      () => this.clearPendingOperation(operation)
    );
  }

  public complete() {
    this.subject.complete();
  }

  private clearPendingOperation<T>(operation: Observable<T>) {
    this.pendingOperations = this.pendingOperations.filter((activeOperation) => activeOperation !== operation);
  }
}
